import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { WHATSAPP_URL } from "../data/content";
import { useLang } from "../hooks/useLanguage";
import { Reveal } from "./Reveal";
import PhoneField from "./PhoneField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  phone: "",
  email: "",
  properties: "",
  amount: "",
  looking_for: "",
  message: "",
  consent: false,
  company_website: "",
};

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-ivory/55 mb-1">
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-2 text-xs text-[#d9a48f]" role="alert">
        {error}
      </p>
    )}
  </div>
);

const Consultation = ({ prefill }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const { t } = useLang();

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, looking_for: prefill }));
  }, [prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = t.UI.errName;
    if (!form.phone || !isValidPhoneNumber(form.phone)) e.phone = t.UI.errPhone;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = t.UI.errEmail;
    if (form.amount !== "" && Number(form.amount) < 0) e.amount = t.UI.errAmount;
    if (!form.looking_for) e.looking_for = t.UI.errSelect;
    if (!form.consent) e.consent = t.UI.errConsent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (status === "submitting") return;
    if (!validate()) {
      toast.error(t.UI.toastReview);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, amount: form.amount === "" ? null : Number(form.amount) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again."
        );
      }
      setStatus("success");
      toast.success(t.UI.toastSuccess);
    } catch (err) {
      setStatus("error");
      toast.error(err.message || t.UI.toastError);
    }
  };

  return (
    <section id="consultation" className="bg-[#141414] text-ivory py-24 md:py-32" data-testid="consultation-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <Reveal>
          <p className="label-caps">{t.UI.consultLabel}</p>
          <h2 className="mt-4 font-serif-lux font-light text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.08] tracking-tight">
            {t.UI.consultTitle}
          </h2>
          <p className="mt-6 text-ivory/60 leading-relaxed max-w-md">{t.UI.consultBody}</p>
          <div className="mt-10 border-t hairline-dark pt-8">
            <p className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-ivory/45">
              {t.UI.consultPreferTalk}
            </p>
            <div className="mt-5">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-lux btn-lux-outline"
                data-testid="consultation-whatsapp"
              >
                <MessageCircle size={16} />
                {t.UI.consultWhatsapp}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          {status === "success" ? (
            <div className="border border-champagne/40 p-10 sm:p-14" data-testid="enquiry-success">
              <span className="h-1.5 w-1.5 rotate-45 bg-champagne block mb-8" aria-hidden="true" />
              <h3 className="font-serif-lux text-3xl font-light">{t.UI.successTitle}</h3>
              <p className="mt-4 text-ivory/60 leading-relaxed">{t.UI.successBody}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-lux btn-lux-outline"
                  data-testid="success-whatsapp"
                >
                  <MessageCircle size={16} />
                  {t.UI.successWhatsapp}
                </a>
                <button
                  className="btn-lux btn-lux-outline"
                  onClick={() => {
                    setForm(EMPTY);
                    setStatus("idle");
                  }}
                  data-testid="success-reset"
                >
                  {t.UI.successReset}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-8" data-testid="enquiry-form">
              <input
                type="text"
                name="company_website"
                value={form.company_website}
                onChange={(e) => set("company_website", e.target.value)}
                className="absolute -left-[9999px] top-0 h-0 w-0 opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <Field label={t.UI.formName} error={errors.full_name} htmlFor="enq-name">
                <input
                  id="enq-name"
                  type="text"
                  className="field-input"
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  autoComplete="name"
                  data-testid="enquiry-name"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label={t.UI.formPhone} error={errors.phone} htmlFor="enq-phone">
                  <PhoneField value={form.phone} onChange={(v) => set("phone", v)} />
                </Field>
                <Field label={t.UI.formEmail} error={errors.email} htmlFor="enq-email">
                  <input
                    id="enq-email"
                    type="email"
                    dir="ltr"
                    className="field-input"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    autoComplete="email"
                    data-testid="enquiry-email"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label={t.UI.formProperties} error={errors.properties} htmlFor="enq-props">
                  <select
                    id="enq-props"
                    className="field-input"
                    value={form.properties}
                    onChange={(e) => set("properties", e.target.value)}
                    data-testid="enquiry-properties"
                  >
                    <option value="" disabled>
                      {t.UI.formSelect}
                    </option>
                    {t.PROPERTY_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t.UI.formAmount} error={errors.amount} htmlFor="enq-amount">
                  <input
                    id="enq-amount"
                    type="number"
                    min="0"
                    dir="ltr"
                    className="field-input"
                    value={form.amount}
                    onChange={(e) => set("amount", e.target.value)}
                    placeholder={t.UI.formAmountPlaceholder}
                    data-testid="enquiry-amount"
                  />
                </Field>
              </div>
              <Field label={t.UI.formLookingFor} error={errors.looking_for} htmlFor="enq-type">
                <select
                  id="enq-type"
                  className="field-input"
                  value={form.looking_for}
                  onChange={(e) => set("looking_for", e.target.value)}
                  data-testid="enquiry-type"
                >
                  <option value="" disabled>
                    {t.UI.formSelect}
                  </option>
                  {t.FINANCE_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t.UI.formMessage} htmlFor="enq-message">
                <textarea
                  id="enq-message"
                  rows={4}
                  className="field-input resize-none"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="enquiry-message"
                />
              </Field>
              <div>
                <label className="flex items-start gap-4 cursor-pointer" data-testid="enquiry-consent-label">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 appearance-none border border-ivory/40 checked:bg-champagne checked:border-champagne transition-colors duration-300 cursor-pointer"
                    data-testid="enquiry-consent"
                  />
                  <span className="text-xs text-ivory/55 leading-relaxed">{t.UI.formConsent}</span>
                </label>
                {errors.consent && (
                  <p className="mt-2 text-xs text-[#d9a48f]" role="alert">
                    {errors.consent}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="btn-lux btn-lux-solid w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="enquiry-submit"
              >
                {status === "submitting" ? t.UI.formSubmitting : t.UI.formSubmit}
                {status !== "submitting" && <ArrowUpRight size={16} className="rtl:-scale-x-100" />}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default Consultation;
